import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Printer, Mail } from 'lucide-react';
import { getServiceOrder, updateServiceOrder, updateServiceOrderStatus, updateSubOrderStatus, addServiceOrderNote, getAllProducts, checkCashRegister, downloadServiceReceipt, sendServiceReceipt, getCompanyConfig } from '../lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'RECIBIDO',   label: 'Recibido' },
  { value: 'EN_PROCESO', label: 'En proceso' },
  { value: 'LISTO',      label: 'Listo' },
  { value: 'ENTREGADO',  label: 'Entregado' },
  { value: 'CANCELADO',  label: 'Cancelado' },
];

const STATUS_COLORS = {
  RECIBIDO:   'bg-blue-100 text-blue-800 border-blue-200',
  EN_PROCESO: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LISTO:      'bg-green-100 text-green-800 border-green-200',
  ENTREGADO:  'bg-slate-100 text-slate-700 border-slate-200',
  CANCELADO:  'bg-red-100 text-red-700 border-red-200',
};

const BANNER_COLORS = {
  RECIBIDO:   'bg-blue-50 border-blue-200 text-blue-900',
  EN_PROCESO: 'bg-yellow-50 border-yellow-200 text-yellow-900',
  LISTO:      'bg-green-50 border-green-200 text-green-900',
  ENTREGADO:  'bg-slate-50 border-slate-200 text-slate-700',
  CANCELADO:  'bg-red-50 border-red-200 text-red-900',
};

const VALID_TRANSITIONS = {
  RECIBIDO:   ['EN_PROCESO', 'LISTO', 'ENTREGADO', 'CANCELADO'],
  EN_PROCESO: ['LISTO', 'ENTREGADO', 'CANCELADO'],
  LISTO:      ['ENTREGADO', 'EN_PROCESO', 'RECIBIDO', 'CANCELADO'],
};

const isService = (product) => {
  const type = String(product?.product_type || product?.type || '').trim().toUpperCase();
  return (
    type === 'SERVICIO' ||
    type === 'SERVICE'  ||
    product?.track_inventory === false ||
    product?.track_inventory === 'false' ||
    product?.track_inventory === 0
  );
};

const money = (n) => Number(n || 0).toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });

const ServiceOrderDetail = () => {
  const { id }   = useParams();
  const navigate  = useNavigate();

  const [order, setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [cajaOpen, setCajaOpen] = useState(true);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [products, setProducts] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const [changingStatus, setChangingStatus]       = useState(false);
  const [statusToSet, setStatusToSet]             = useState('');
  const [deliverWithDebtOpen, setDeliverWithDebtOpen] = useState(false);
  const [changingSubOrder, setChangingSubOrder]   = useState(null);
  const [cancelOpen, setCancelOpen]               = useState(false);
  const [cancelReason, setCancelReason]           = useState('');
  const [creatingNc, setCreatingNc]               = useState(false);

  const [editingNotes, setEditingNotes] = useState(false);
  const [notesInput, setNotesInput]     = useState('');

  const [noteInput, setNoteInput]   = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const [printingReceipt, setPrintingReceipt] = useState(false);
  const [sendEmailModal, setSendEmailModal]   = useState(false);
  const [emailTo, setEmailTo]                 = useState('');
  const [cardSurchargePct, setCardSurchargePct] = useState(0);
  const [paymentMethod, setPaymentMethod]       = useState('efectivo');
  const [sendingEmail, setSendingEmail]       = useState(false);

  const statusLabel = useMemo(() => {
    const m = new Map(STATUS_OPTIONS.map(s => [s.value, s.label]));
    return (s) => m.get(s) || s;
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const o = await getServiceOrder(id);
      setOrder(o);
      setStatusToSet(o.status || '');
      setEditForm({
        item_product_name:  o.item_product_name  || '',
        item_variant_size:  o.item_variant_size  || '',
        item_variant_color: o.item_variant_color || '',
        item_description:   o.item_description   || '',
        work_notes:         o.work_notes         || '',
        due_date:           o.due_date ? o.due_date.slice(0, 10) : '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error cargando la orden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    checkCashRegister()
      .then(res => setCajaOpen(!res.required || res.open))
      .catch(() => setCajaOpen(true));
    getCompanyConfig().then(cfg => setCardSurchargePct(parseFloat(cfg?.card_surcharge_pct || 0))).catch(() => {});
    (async () => {
      try {
        const prods = await getAllProducts();
        setProducts(Array.isArray(prods) ? prods : (prods?.products || []));
      } catch {}
    })();
    // eslint-disable-next-line
  }, [id]);

  const serviceProducts = useMemo(() => products.filter(p => isService(p)), [products]);

  const handlePrintReceipt = async () => {
    setPrintingReceipt(true);
    try {
      const blob = await downloadServiceReceipt(id);
      if (blob.type === 'application/json') {
        const json = JSON.parse(await blob.text());
        toast.error(json.error || 'Error generando recibo');
        return;
      }
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 3000);
    } catch (err) {
      toast.error(err.message || 'Error generando recibo');
    } finally {
      setPrintingReceipt(false);
    }
  };

  const handleSendEmail = async () => {
    const email = emailTo.trim();
    if (!email || !email.includes('@')) { toast.error('Ingresa un correo válido'); return; }
    setSendingEmail(true);
    try {
      await sendServiceReceipt(id, email);
      toast.success(`Recibo enviado a ${email}`);
      setSendEmailModal(false);
      setEmailTo('');
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Error enviando correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const doStatusChange = async (newStatus) => {
    if (newStatus === 'CANCELADO') {
      setCancelOpen(true);
      return;
    }
    if (newStatus === 'ENTREGADO') {
      const balance = parseFloat(order?.pricing?.balance || 0);
      if (balance > 0.01) {
        setDeliverWithDebtOpen(true);
        return;
      }
    }
    await applyStatusChange(newStatus);
  };

  const applyStatusChange = async (newStatus) => {
    try {
      setChangingStatus(true);
      const updated = await updateServiceOrderStatus(id, newStatus);
      setOrder(updated);
      setStatusToSet(updated.status || newStatus);
      toast.success(`Estatus cambiado a ${statusLabel(newStatus)}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error cambiando estatus');
    } finally {
      setChangingStatus(false);
    }
  };

  const confirmCancel = async () => {
    const reason = cancelReason.trim();
    if (!reason) return toast.error('La razón es obligatoria');
    try {
      setCreatingNc(true);
      const updated = await updateServiceOrderStatus(id, 'CANCELADO', reason);
      setOrder(updated);
      setCancelOpen(false);
      setCancelReason('');

      if (updated?.auto_credit_note) {
        toast.success(`Orden cancelada · Nota de crédito ${updated.auto_credit_note.credit_note_number} creada`);
      } else {
        toast.success('Orden cancelada');
      }
      navigate('/service-orders');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error cancelando orden');
    } finally {
      setCreatingNc(false);
    }
  };

  const addNote = async () => {
    if (!noteInput.trim()) return;
    try {
      setAddingNote(true);
      const updated = await addServiceOrderNote(id, noteInput.trim());
      setOrder(updated);
      setNoteInput('');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error agregando nota');
    } finally {
      setAddingNote(false);
    }
  };

  const saveNotes = async () => {
    try {
      setSavingEdit(true);
      const updated = await updateServiceOrder(id, { work_notes: notesInput });
      setOrder(updated);
      setEditingNotes(false);
      toast.success('Notas actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error guardando notas');
    } finally {
      setSavingEdit(false);
    }
  };

  const doSubOrderStatusChange = async (subOrderId, newStatus) => {
    try {
      setChangingSubOrder(subOrderId);
      const updated = await updateSubOrderStatus(id, subOrderId, newStatus);
      setOrder(updated);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error cambiando estatus');
    } finally {
      setChangingSubOrder(null);
    }
  };

  const saveEdits = async () => {
    try {
      setSavingEdit(true);
      const payload = { ...editForm };
      const updated = await updateServiceOrder(id, payload);
      setOrder(updated);
      setEditing(false);
      toast.success('Cambios guardados');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error guardando cambios');
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Cargando...</div>;
  if (!order)  return <div className="p-6 text-slate-500">Orden no encontrada.</div>;

  const total      = Number(order.pricing?.total_amount || 0);
  const paid       = Number(order.pricing?.paid_amount  || 0);
  const balance    = Math.max(0, total - paid);
  const CARD_FACTOR   = cardSurchargePct > 0 ? (1 + cardSurchargePct / 100) : 1;
  const withSurcharge = (n) => cardSurchargePct > 0 ? Math.round(n * CARD_FACTOR * 100) / 100 : n;
  const isCardPayment = paymentMethod === 'tarjeta' && cardSurchargePct > 0;
  const embInvoices     = order.invoices || [];
  const displayPaid   = embInvoices.reduce(
    (sum, inv) => sum + (Number(inv.total || 0) - Number(inv.itbis || 0) + Number(inv.discount || 0)), 0
  );
  const displayBalance = isCardPayment ? withSurcharge(balance) : balance;
  const displayTotal   = displayPaid + displayBalance;
  const services   = order.services || [];
  const subOrders  = order.sub_orders || [];
  const isNewFormat = subOrders.length > 0;
  const isFinal       = order.status === 'CANCELADO';
  const isEntregado   = order.status === 'ENTREGADO';
  const canEdit     = !isFinal && !isEntregado;
  const allowedNext = VALID_TRANSITIONS[order.status] || [];

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto" data-testid="service-order-detail-page">
      {/* Razón de cancelación */}
      {order.status === 'CANCELADO' && order.cancel_reason && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
          <span className="font-semibold">Motivo de cancelación: </span>{order.cancel_reason}
        </div>
      )}

      {/* Status banner */}
      <div className={`border rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 justify-between ${BANNER_COLORS[order.status] || 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-bold text-lg" style={{ fontFamily: 'Outfit' }}>
            {order.code}
          </span>
          {!isFinal && !isEntregado ? (
            <>
              <Select value={statusToSet} onValueChange={setStatusToSet} disabled={changingStatus}>
                <SelectTrigger className="w-44 h-8 text-xs bg-white/70">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem
                      key={s.value}
                      value={s.value}
                      className={s.value === 'CANCELADO' ? 'text-red-600' : ''}
                    >
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => statusToSet && statusToSet !== order.status && doStatusChange(statusToSet)}
                disabled={changingStatus || !statusToSet || statusToSet === order.status}
                className="h-8 text-xs"
              >
                {changingStatus ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </>
          ) : (
            <span className={`inline-block rounded-full px-3 py-0.5 text-xs font-semibold border ${STATUS_COLORS[order.status] || ''}`}>
              {statusLabel(order.status)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrintReceipt} disabled={printingReceipt} title="Imprimir recibo">
            <Printer className="w-4 h-4 mr-1" />
            {printingReceipt ? 'Generando...' : 'Imprimir'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setEmailTo(order.customer_email || ''); setSendEmailModal(true); }} title="Enviar recibo por correo">
            <Mail className="w-4 h-4 mr-1" />
            Correo
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/service-orders')}>
            ← Volver
          </Button>
        </div>
      </div>

      {/* Info general */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Información general</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Cliente</p>
            <p className="font-medium">{order.customer_name}</p>
            {order.customer_phone && <p className="text-slate-400">{order.customer_phone}</p>}
          </div>
          <div>
            <p className="text-slate-500">Recibido</p>
            <p className="font-medium">
              {order.received_at ? new Date(order.received_at).toLocaleString('es-DO') : '-'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Fecha de entrega</p>
            <p className="font-medium">
              {order.due_date ? new Date(order.due_date).toLocaleDateString('es-DO') : '-'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Sucursal</p>
            <p className="font-medium">{order.branch_name || order.branch_id}</p>
          </div>
          <div>
            <p className="text-slate-500">Creado por</p>
            <p className="font-medium">{order.created_by_name || '-'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Prendas — new sub-orders format */}
      {isNewFormat ? (
        <Card>
          <CardHeader><CardTitle className="text-lg">Prendas ({subOrders.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {subOrders.map((so, idx) => {
              const soStatus = so.status || 'RECIBIDO';
              const soStatusColors = {
                RECIBIDO:   'bg-blue-100 text-blue-700 border-blue-200',
                EN_PROCESO: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                LISTO:      'bg-green-100 text-green-700 border-green-200',
              };
              const soNextStatus = {
                RECIBIDO:   'EN_PROCESO',
                EN_PROCESO: 'LISTO',
                LISTO:      null,
              };
              const soNextLabel = {
                RECIBIDO:   '→ En proceso',
                EN_PROCESO: '→ Listo',
              };
              const nextStatus = soNextStatus[soStatus];
              const isUpdating = changingSubOrder === so.id;

              return (
                <div key={so.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="bg-cyan-100 text-cyan-800 font-bold text-xs rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                        {so.letter || String.fromCharCode(65 + idx)}
                      </span>
                      <span className="font-semibold text-sm text-slate-900">{so.product_name || '-'}</span>
                      {so.item_type === 'external' && (
                        <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">Externo</span>
                      )}
                      {(so.variant_size || so.variant_color) && (
                        <span className="text-xs text-slate-400">
                          {[so.variant_size, so.variant_color].filter(Boolean).join(' / ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${soStatusColors[soStatus] || ''}`}>
                        {soStatus === 'RECIBIDO' ? 'Recibido' : soStatus === 'EN_PROCESO' ? 'En proceso' : 'Listo'}
                      </span>
                      {nextStatus && !isFinal && !isEntregado && (
                        <button
                          type="button"
                          onClick={() => doSubOrderStatusChange(so.id, nextStatus)}
                          disabled={isUpdating}
                          className="text-xs px-2 py-0.5 border border-slate-300 rounded hover:bg-cyan-50 hover:border-cyan-400 hover:text-cyan-700 transition-colors disabled:opacity-50">
                          {isUpdating ? '...' : soNextLabel[soStatus]}
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Body */}
                  <div className="px-4 py-3 space-y-1.5 text-sm">
                    {so.item_description && (
                      <p className="text-xs text-slate-400 italic">{so.item_description}</p>
                    )}
                    <div className="space-y-0.5">
                      {Number(so.item_price) > 0 && (
                        <div className="flex justify-between text-slate-700">
                          <span className="flex items-center gap-1">
                            • {so.product_name}
                            <span className="text-xs text-slate-400">(prenda)</span>
                          </span>
                          <span className="font-medium">{money(so.item_price)}</span>
                        </div>
                      )}
                      {(so.services || []).map(s => (
                        <div key={s.product_id} className="flex justify-between text-slate-700">
                          <span>• {s.name} ×{s.quantity}</span>
                          <span className="font-medium">{money(s.subtotal)}</span>
                        </div>
                      ))}
                    </div>
                    {so.work_notes && (
                      <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-1.5 mt-1.5">
                        "{so.work_notes}"
                      </p>
                    )}
                    <div className="flex justify-end pt-1 border-t border-slate-100">
                      <span className="font-semibold text-slate-800">
                        Subtotal: {money(so.subtotal)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Old format: Prenda + Servicios */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg">Prenda dejado</CardTitle>
              {canEdit && !editing && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>Editar</Button>
              )}
              {editing && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdits} disabled={savingEdit}>
                    {savingEdit ? 'Guardando...' : 'Guardar'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={savingEdit}>
                    Cancelar
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!editing ? (
                <>
                  <div>
                    <span className="text-slate-500">Tipo: </span>
                    <span className="font-medium">{order.item_type === 'catalog' ? 'Del catálogo' : 'Prenda externo'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Prenda: </span>
                    <span className="font-medium">{order.item_product_name || '-'}</span>
                    {(order.item_variant_size || order.item_variant_color) && (
                      <span className="ml-2 text-slate-400">
                        {[order.item_variant_size, order.item_variant_color].filter(Boolean).join(' / ')}
                      </span>
                    )}
                  </div>
                  {order.item_description && (
                    <div>
                      <span className="text-slate-500">Descripción: </span>
                      <span className="text-slate-700">{order.item_description}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Nombre del prenda</Label>
                    <Input value={editForm.item_product_name}
                      onChange={e => setEditForm(p => ({ ...p, item_product_name: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Talla</Label>
                    <Input value={editForm.item_variant_size}
                      onChange={e => setEditForm(p => ({ ...p, item_variant_size: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Color</Label>
                    <Input value={editForm.item_variant_color}
                      onChange={e => setEditForm(p => ({ ...p, item_variant_color: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2 space-y-1">
                    <Label>Descripción del prenda</Label>
                    <Textarea value={editForm.item_description}
                      onChange={e => setEditForm(p => ({ ...p, item_description: e.target.value }))} rows={2} />
                  </div>
                  <div className="space-y-1">
                    <Label>Fecha de entrega</Label>
                    <Input type="date" value={editForm.due_date}
                      onChange={e => setEditForm(p => ({ ...p, due_date: e.target.value }))} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg">Servicios</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-600">
                      <th className="py-2 pr-4">Servicio</th>
                      <th className="py-2 pr-4">Cantidad</th>
                      <th className="py-2 pr-4">Precio unitario</th>
                      <th className="py-2 pr-4">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map(s => (
                      <tr key={s.product_id} className="border-b">
                        <td className="py-2 pr-4">{s.name}</td>
                        <td className="py-2 pr-4">{s.quantity}</td>
                        <td className="py-2 pr-4">{money(s.unit_price)}</td>
                        <td className="py-2 pr-4 font-medium">{money(s.subtotal)}</td>
                      </tr>
                    ))}
                    {services.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-4 text-center text-slate-500">Sin servicios registrados.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {services.length > 0 && (
                <div className="mt-3 text-right text-sm font-medium text-slate-700">
                  Total servicios: <span className="text-base font-bold">{money(order.pricing?.services_total || 0)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Instrucciones generales */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg">Notas generales</CardTitle>
          {canEdit && !editingNotes && !order.work_notes && (
            <button
              type="button"
              onClick={() => { setNotesInput(''); setEditingNotes(true); }}
              className="text-xs text-cyan-600 hover:text-cyan-800 font-medium transition-colors"
            >
              + Agregar nota
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {order.work_notes && !editingNotes && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.work_notes}</p>
          )}
          {!order.work_notes && !editingNotes && (
            <p className="text-sm text-slate-400 italic">Sin instrucciones. Usa el botón para agregar.</p>
          )}
          {editingNotes && (
            <>
              <Textarea
                autoFocus
                rows={3}
                value={notesInput}
                onChange={e => setNotesInput(e.target.value)}
                placeholder="Instrucciones generales para el taller..."
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveNotes} disabled={savingEdit}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white">
                  {savingEdit ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditingNotes(false)} disabled={savingEdit}>
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Notas de seguimiento */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Notas de seguimiento</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(order.notes || []).length > 0 ? (
            <div className="space-y-2">
              {order.notes.map(n => (
                <div key={n.id} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                  <p className="text-sm text-slate-800">{n.text}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {n.created_by} · {new Date(n.created_at).toLocaleString('es-DO')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Sin notas de seguimiento.</p>
          )}

          {!isFinal && (
            <div className="flex gap-2 items-end pt-1">
              <Textarea
                rows={2}
                className="flex-1 resize-none"
                placeholder="Ej: Cliente cambió el color del hilo a rojo..."
                value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && e.ctrlKey && addNote()}
              />
              <Button
                type="button"
                onClick={addNote}
                disabled={addingNote || !noteInput.trim()}
                className="bg-cyan-500 hover:bg-cyan-600 text-white self-end"
              >
                {addingNote ? '...' : 'Agregar'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resumen de pago */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Resumen de pago</CardTitle></CardHeader>
        <CardContent>
          {cardSurchargePct > 0 && balance > 0 && !isFinal && (
            <div className="flex gap-2 mb-4">
              {[{ value: 'efectivo', label: '💵 Efectivo' }, { value: 'tarjeta', label: '💳 Tarjeta' }].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setPaymentMethod(opt.value)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    paymentMethod === opt.value
                      ? 'bg-cyan-500 text-white border-cyan-500'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400 hover:text-cyan-600'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Total</p>
              <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>{money(displayTotal)}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Pagado</p>
              <p className="text-xl font-bold text-green-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(displayPaid)}</p>
            </div>
            <div className={`border rounded-xl p-4 text-center ${balance > 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <p className="text-xs text-slate-500 mb-1">Balance pendiente</p>
              <p className={`text-xl font-bold ${balance > 0 ? 'text-amber-700' : 'text-slate-500'}`} style={{ fontFamily: 'JetBrains Mono' }}>
                {money(displayBalance)}
              </p>
              {isCardPayment && balance > 0 && (
                <p className="text-xs text-slate-400 mt-0.5">{money(balance)} efectivo</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-1">
            {balance > 0 && !isFinal && (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
                onClick={() => {
                  if (!cajaOpen) { toast.warning('Abre un turno de caja para facturar'); return; }
                  navigate(`/pos?service_order_id=${order.id}&initial_payment=${balance.toFixed(2)}&payment_method=${paymentMethod}`);
                }}
              >
                {cajaOpen ? '💰 Cobrar balance' : '🔒 Cobrar balance'}
              </Button>
            )}
            {balance > 0 && isEntregado && (
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
                onClick={() => {
                  if (!cajaOpen) { toast.warning('Abre un turno de caja para facturar'); return; }
                  navigate(`/pos?service_order_id=${order.id}&initial_payment=${balance.toFixed(2)}&payment_method=${paymentMethod}`);
                }}
              >
                {cajaOpen ? '💰 Cobrar balance pendiente' : '🔒 Cobrar balance pendiente'}
              </Button>
            )}
            <Button variant="outline" onClick={handlePrintReceipt} disabled={printingReceipt}>
              <Printer className="w-4 h-4 mr-1" />
              {printingReceipt ? 'Generando...' : 'Imprimir recibo'}
            </Button>
            <Button variant="outline" onClick={() => { setEmailTo(order.customer_email || ''); setSendEmailModal(true); }}>
              <Mail className="w-4 h-4 mr-1" />
              Enviar por correo
            </Button>
          </div>
        </CardContent>
      </Card>

      {embInvoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Historial de pagos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-600">
                    <th className="py-2 pr-4">Fecha</th>
                    <th className="py-2 pr-4">Factura</th>
                    <th className="py-2 pr-4">Método</th>
                    <th className="py-2 pr-4">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {embInvoices.map((inv, idx) => {
                    const methodLabels = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', credito: 'Crédito' };
                    const methodText = inv.payment_methods?.length > 0
                      ? inv.payment_methods.map(pm => methodLabels[pm.method] || pm.method).join(' + ')
                      : methodLabels[inv.payment_method] || inv.payment_method || '-';
                    return (
                      <tr key={inv.id || idx} className="border-b">
                        <td className="py-2 pr-4">{inv.created_at ? new Date(inv.created_at).toLocaleString('es-DO') : ''}</td>
                        <td className="py-2 pr-4">{inv.invoice_number || inv.id || ''}</td>
                        <td className="py-2 pr-4 text-slate-600">{methodText}</td>
                        <td className="py-2 pr-4">{money((Number(inv.total || inv.amount_applied || 0)) - (Number(inv.itbis || 0)) + (Number(inv.discount || 0)))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Modal enviar recibo por correo */}
      <Dialog open={sendEmailModal} onOpenChange={setSendEmailModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enviar recibo por correo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Correo electrónico</Label>
            <Input
              type="email"
              placeholder="cliente@correo.com"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendEmailModal(false)} disabled={sendingEmail}>
              Cancelar
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-cyan-500 hover:bg-cyan-600 text-white">
              {sendingEmail ? 'Enviando...' : 'Enviar recibo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog cancelación */}
      <Dialog open={cancelOpen} onOpenChange={(v) => { setCancelOpen(v); if (!v) setCancelReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar orden de servicio</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Indica el motivo. Esta acción restaurará el inventario si aplica y bloqueará modificaciones.
            </p>
            {Number(order?.pricing?.paid_amount || 0) > 0.01 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                Esta orden tiene <strong>RD${Number(order?.pricing?.paid_amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</strong> cobrado.
                Se emitirá automáticamente una nota de crédito al cancelar.
              </div>
            )}
            <div className="space-y-1">
              <Label>Razón *</Label>
              <Input
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && confirmCancel()}
                placeholder="Ej: Cliente desistió / error en la orden / etc."
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setCancelOpen(false); setCancelReason(''); }}>
                Volver
              </Button>
              <Button variant="destructive" onClick={confirmCancel} disabled={creatingNc}>
                {creatingNc ? 'Procesando...' : Number(order?.pricing?.paid_amount || 0) > 0.01 ? 'Cancelar y emitir NC' : 'Confirmar cancelación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog entregar con balance pendiente */}
      <Dialog open={deliverWithDebtOpen} onOpenChange={setDeliverWithDebtOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Balance pendiente</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-700">
              Esta orden tiene un balance pendiente de{' '}
              <span className="font-bold text-red-600">
                {money(order?.pricing?.balance)}
              </span>
              . La orden será marcada como <strong>ENTREGADO</strong>.
            </p>
            <p className="text-sm text-slate-500">
              La comisión se generará cuando el balance quede en cero.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setDeliverWithDebtOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              onClick={async () => {
                setDeliverWithDebtOpen(false);
                await applyStatusChange('ENTREGADO');
                navigate(`/pos?service_order_id=${id}`);
              }}
            >
              Ir a cobrar
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={async () => {
                setDeliverWithDebtOpen(false);
                await applyStatusChange('ENTREGADO');
              }}
            >
              Entregar sin cobrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceOrderDetail;
